import { plural } from "@lingui/core/macro";
import { SelectOrdinal, Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useState } from "react";

import { ApiConnector, MedicationDto } from "../../../api/api-connector";
import { useSupportedMedications } from "../../../catalogs/supported-medications";
import { Button } from "../../../components/ui-kit/button";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { CellDate } from "../../../components/ui-kit/tables/cells/cell-date";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { Patient } from "../patient";
import {
  applyHasEndedToPatch,
  draftHasEnded,
  MedicationEndedField,
  MedicationStartedField,
  setDraftHasEnded,
} from "./medication-period-fields";
import { Medications } from "./medications";

export const MedicationsDataTable = observer(
  function MedicationsDataTable(props: {
    data: MedicationDto[];
    api: ApiConnector;
    patient: Patient;
    onInvalidated?: () => void;
    disableEdit?: boolean;
    disableHistory?: boolean;
    disablePagination?: boolean;
    persistState?: boolean;
    filterActions?: ReactNode;
  }) {
    const { t } = useLingui();
    const medications = useSupportedMedications();
    const [model] = useState(() => {
      const m = new Medications(
        [],
        props.api.permissions,
        props.api.requireUserUUID(),
      );
      if (props.disableHistory) {
        m.hasHistory = false;
      }
      return m;
    });

    useEffect(() => {
      model.updateData(props.data);
    }, [props.data, model]);

    return (
      <DataTable
        filterActions={props.filterActions}
        persistState={props.persistState}
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "medication", other: "medications" })}`
        }
        columns={[
          {
            header: <Trans>Started</Trans>,
            attr: "medication_started",
            type: "date",
            editable: !props.disableEdit,
            sort: true,
            initSort: "desc",
            // Hosts the "Has ended" checkbox, which gates the Ended field
            // below. Only the edit dialog ever passes a `patch` for this
            // column, so the table itself still renders a plain date.
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <MedicationStartedField
                  model={patch}
                  hasEnded={draftHasEnded(patch, item.medication_ended)}
                  onHasEndedChange={(hasEnded) =>
                    setDraftHasEnded(patch, hasEnded)
                  }
                />
              ) : (
                <CellDate item={item} attr="medication_started" type="date" />
              ),
          },
          {
            header: <Trans>Ended</Trans>,
            attr: "medication_ended",
            type: "date",
            editable: !props.disableEdit,
            sort: true,
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <MedicationEndedField
                  model={patch}
                  hasEnded={draftHasEnded(patch, item.medication_ended)}
                  onHasEndedChange={(hasEnded) =>
                    setDraftHasEnded(patch, hasEnded)
                  }
                />
              ) : (
                <CellDate item={item} attr="medication_ended" type="date" />
              ),
          },
          {
            header: <Trans>Group</Trans>,
            attr: "medication_group",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Medication`,
              options: uniqueValueOptions("medication_group"),
            },
          },
          {
            header: <Trans>Name</Trans>,
            attr: "medication_name",
            type: "string",
            sort: true,
            // Free-text names can be corrected in the edit dialog; recognized
            // catalog names are fixed.
            editable: (item) =>
              !props.disableEdit &&
              !medications.isKnownMedication(
                item.medication_group,
                item.medication_name,
              ),
          },
          {
            header: <Trans>Dose</Trans>,
            attr: "medication_dose",
            type: "number",
            editable: !props.disableEdit,
          },
          {
            header: <Trans>Unit</Trans>,
            attr: "medication_unit",
            type: "string",
            editable: !props.disableEdit,
          },
          {
            header: <Trans>Dosage</Trans>,
            attr: "medication_dosage",
            editable: !props.disableEdit,
            editInDialog: true,
            // Driven by `patch` rather than the model's draft directly, so the
            // same editor serves the edit dialog and the inline "Change dosage"
            // flow.
            cell: ({ item, patch }) =>
              patch ? (
                <span className="inline-flex items-center gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <NumberInput
                      key={i}
                      className="number-input"
                      min={0}
                      value={patch.medication_dosage[i]}
                      onChange={(newValue) => {
                        patch.medication_dosage[i] = newValue ?? 0;
                      }}
                      width={50}
                    />
                  ))}
                </span>
              ) : (
                <span>{item.medication_dosage.join("/")}</span>
              ),
          },
          {
            header: <Trans>Frequency</Trans>,
            attr: "medication_frequency_regular_repeat_every",
            // Only "every N days" medications are editable here. A
            // day-scheduled one (`medication_frequency_on_days`) can't be
            // expressed by this editor, and setting a repeat interval on it
            // would leave the record carrying two conflicting schedules — the
            // API accepts both fields at once. Gated on the saved row, which is
            // what `editable` receives, so clearing the input mid-edit can't
            // make the field vanish from the dialog. The "Change dosage" flow
            // names this field too, and honours the same gate (see
            // `singleEditing` in editable-cell).
            editable: (item) =>
              !props.disableEdit &&
              item.medication_frequency_regular_repeat_every != null,
            editInDialog: true,
            cell: ({ item, patch }) =>
              patch ? (
                <span className="inline-flex items-center gap-1">
                  {/* One message rather than "Every" + input + "day" as three,
                      so a locale controls word order and case agreement — the
                      split read "Alle 3 Tag" in German. The suffix hugs the
                      input so the editor reads as the cell does: "Every 3rd
                      day", not "Every 3 day", which would look like a
                      different schedule. */}
                  <Trans>
                    Every{" "}
                    <span className="inline-flex items-center">
                      <NumberInput
                        model={patch}
                        attr="medication_frequency_regular_repeat_every"
                        min={1}
                        // A repeat interval is a whole number of days;
                        // NumberInput otherwise defaults to step="any".
                        step={1}
                        // The column is only editable for a medication that
                        // already repeats, so an empty input is never a valid
                        // submission — and an undefined value would reach the
                        // API as a PUT with no body at all, saving nothing
                        // without saying so.
                        required
                        width={50}
                      />
                      {patch.medication_frequency_regular_repeat_every !=
                        null && (
                        <SelectOrdinal
                          value={
                            patch.medication_frequency_regular_repeat_every as number
                          }
                          one="st"
                          two="nd"
                          few="rd"
                          other="th"
                        />
                      )}
                    </span>{" "}
                    day
                  </Trans>
                </span>
              ) : (
                <span>{model.freqString(item)}</span>
              ),
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: true,
            editable: !props.disableEdit,
          },
          !props.disableEdit && {
            header: <Trans>Change dosage</Trans>,
            sticky: "right",
            width: 80,
            cell: ({ item }) => (
              <>
                {model.isEditingItem(item) &&
                model.editingDraft &&
                "medication_change_date" in model.editingDraft ? (
                  <DateInput
                    type="date"
                    model={model.editingDraft}
                    attr="medication_change_date"
                  />
                ) : (
                  !model.isEditingItem(item) && (
                    <Button
                      onClick={() => {
                        model.startChangingDosage(item);
                      }}
                      data-tooltip={t`Use when adapting dose, dosage and / or frequency for this medication.`}
                      variant="subtle"
                    >
                      <Trans>Change</Trans>
                    </Button>
                  )
                )}
              </>
            ),
          },
          model.showingHistory && {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
          },
          model.showingHistory && {
            header: <Trans>Last edit</Trans>,
            cell: ({ item }) => <CellLastEdit item={item} />,
          },
        ]}
        onEdit={
          props.disableEdit
            ? undefined
            : async (editedField, item) => {
                if ("medication_change_date" in editedField) {
                  await props.patient.updateItem(
                    "medications/change",
                    item.uuid,
                    editedField,
                  );
                } else {
                  const body = applyHasEndedToPatch(editedField);
                  // Toggling "Has ended" off and back on leaves the record as
                  // it was, so the patch holds nothing but the UI-only key —
                  // and nothing at all once that is stripped.
                  if (Object.keys(body).length === 0) {
                    return;
                  }
                  await props.patient.updateItem(
                    "medications",
                    item.uuid,
                    body,
                    // Correcting a wrongly-entered medication includes taking
                    // its dates back off, so those nulls have to reach the API.
                    // Both are optional on the record, and emptying either
                    // input is the only way to say "unknown".
                    {
                      clearableKeys: ["medication_started", "medication_ended"],
                    },
                  );
                }
                props.onInvalidated?.();
              }
        }
        onDelete={
          props.disableEdit
            ? undefined
            : async (item) => {
                await props.patient.deleteItem("medications", item.uuid);
                props.onInvalidated?.();
              }
        }
        rowsPerPage={props.disablePagination ? Number.MAX_VALUE : 25}
      />
    );
  },
);
